import { useState, type FormEvent } from 'react'

export interface ContactFormValues {
  fullName: string
  email: string
  company: string
  phone: string
  message: string
}

export type ContactFormErrors = Partial<Record<keyof ContactFormValues, string>>

type Status = 'idle' | 'submitting' | 'success' | 'error'

const emptyValues: ContactFormValues = {
  fullName: '',
  email: '',
  company: '',
  phone: '',
  message: '',
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

function validate(values: ContactFormValues): ContactFormErrors {
  const errors: ContactFormErrors = {}
  if (!values.fullName.trim()) errors.fullName = 'Please enter your full name.'
  if (!values.email.trim()) {
    errors.email = 'Please enter your email address.'
  } else if (!EMAIL_PATTERN.test(values.email.trim())) {
    errors.email = 'Please enter a valid email address.'
  }
  if (!values.message.trim()) {
    errors.message = 'Please enter a message.'
  } else if (values.message.trim().length < 10) {
    errors.message = 'Please provide a little more detail.'
  }
  return errors
}

export function useContactForm() {
  const [values, setValues] = useState<ContactFormValues>(emptyValues)
  const [errors, setErrors] = useState<ContactFormErrors>({})
  const [status, setStatus] = useState<Status>('idle')

  const setField = (field: keyof ContactFormValues, value: string) => {
    setValues((prev) => ({ ...prev, [field]: value }))
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev))
  }

  const reset = () => {
    setValues(emptyValues)
    setErrors({})
    setStatus('idle')
  }

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const nextErrors = validate(values)
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) {
      setStatus('idle')
      return
    }

    setStatus('submitting')
    try {
      // The submission destination (email service or form endpoint) is intentionally
      // left configurable in the site's settings and is not hard-coded here.
      await new Promise((resolve) => setTimeout(resolve, 700))
      setStatus('success')
      setValues(emptyValues)
    } catch {
      setStatus('error')
    }
  }

  return { values, errors, status, setField, submit, reset }
}
